<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const channelList = [
  { type: 'Credit Card', img: '/img/bank-assets/BLIBLI.png' },
  { type: 'Credit Card', img: '/img/bank-assets/tokopedia.png' },
  { type: 'Non Credit Card', img: '/img/bank-assets/tokopedia.png' },
  { type: null, img: '/img/bank-assets/QRIS.png' },
  { type: null, img: null },
  { type: 'Credit Card (F&B)', img: '/img/bank-assets/CC.png' },
  { type: 'Virtual Account', img: '/img/bank-assets/va.png' },
  { type: null, img: null },
]

const testimonialList = [
  {
    name: 'Budi Santoso',
    job: 'Owner, Katering \'Dapur Bu Delista\'',
    content: '"Pembayaran ke supplier jadi super praktis! Dulu, bayar tagihan ke banyak supplier itu makan waktu dan sering bikin pusing. Sejak pakai Rintisan, semuanya jadi satu pintu. Tinggal klik, beres! Fitur pembayaran tagihannya benar-benar mengubah cara saya mengelola keuangan."',
  },
  {
    name: 'Clara Wijaya',
    job: 'Founder, \'Titik Kumpul Coffee\'',
    content: '"Membantu cash flow usaha saya tetap lancar. Sebagai pemilik coffee shop, perputaran uang harus cepat. Rintisan sangat membantu karena proses pencairan dananya cepat dan bisa diandalkan. Saya juga suka karena ada banyak pilihan pembayaran untuk pelanggan, jadi mereka lebih mudah bertransaksi."',
  },
  {
    name: 'Andi Pratama',
    job: 'Pemilik, \'Gear Up!\'',
    content: '"Aplikasi simpel yang powerful. Saya butuh solusi pembayaran yang nggak ribet dan mudah dimengerti. Rintisan menjawab semua itu. Tampilannya sederhana, fiturnya langsung ke intinya. Dari menerima pembayaran via QRIS sampai bayar vendor, semua lancar jaya. Sangat direkomendasikan untuk UMKM!"',
  },
  {
    name: 'Rina Marlina',
    job: 'Designer & Owner, \'Eleganza Boutique\'',
    content: '"Akhirnya bisa terima pembayaran kartu kredit tanpa pusing. Bisnis fashion saya sering menerima pesanan dalam jumlah besar, dan banyak pelanggan lebih suka bayar pakai kartu kredit. Rintisan memfasilitasi ini dengan sangat mudah dan dengan biaya yang transparan. Benar-benar game-changer!"',
  },
  {
    name: 'Aditya Nugraha',
    job: 'Co-founder, \'Pixel Perfect Agency\'',
    content: '"Fitur penagihan invoice-nya juara! Kami bergerak di bidang jasa kreatif dan sering mengirim invoice ke klien. Fitur penagihan di Rintisan membuat prosesnya jadi profesional dan mudah dilacak. Klien bisa langsung bayar dari link yang dikirim, dan notifikasinya real-time."',
  },
]

const currentIndex = ref(0)
const currentTestimonial = computed(() => testimonialList[currentIndex.value])
let autoplayTimer: ReturnType<typeof setInterval> | null = null

function prev() {
  currentIndex.value = (currentIndex.value - 1 + testimonialList.length) % testimonialList.length
}

function next() {
  currentIndex.value = (currentIndex.value + 1) % testimonialList.length
}

function goTo(index: number) {
  currentIndex.value = index
}

function startAutoplay() {
  autoplayTimer = setInterval(next, 4000)
}

function stopAutoplay() {
  if (autoplayTimer)
    clearInterval(autoplayTimer)
}

onMounted(() => startAutoplay())
onBeforeUnmount(() => stopAutoplay())
</script>

<template>
  <div class="bg-[#fafafa]">
    <!-- Payment Channels -->
    <section id="partner" class="container mx-auto xs:py-[50px] py-[80px] xs:px-4">
      <AnimateOnce initial-class="opacity-0 translate-y-8" animated-class="opacity-100 translate-y-0" :duration="600">
        <h2 class="text-center text-neutral-800 text-[32px] font-medium font-geist">
          Pilihan Channel Pembayaran
        </h2>
      </AnimateOnce>
      <div class="grid grid-cols-4 mt-[42px] gap-5">
        <AnimateOnce
          v-for="(channel, i) in channelList"
          :key="i"
          initial-class="opacity-0 scale-95"
          animated-class="opacity-100 scale-100"
          :duration="500"
          :delay="i * 60"
          :class="!channel.img ? 'invisible xs:hidden' : ''"
          class-name="border border-neutral-200 xs:col-span-4 rounded-xl bgWhiteGradient overflow-hidden px-6 py-5 xs:h-[150px] h-[180px] w-full relative"
        >
          <div class="shadow-inners rounded-xl" />
          <div class="flex flex-col items-center justify-center h-full">
            <img
              v-if="channel.img"
              :src="channel.img"
              alt="channel"
              :class="channel.type === 'Virtual Account' ? 'w-[60px] h-[45px]' : 'w-[130px]'"
              class="object-contain"
            >
            <p v-if="channel.type" class="mt-2 text-xs font-normal text-neutral-500 text-center">
              {{ channel.type }}
            </p>
          </div>
          <img
            class="absolute bottom-0 right-0 z-0 object-contain w-[100px]"
            src="/img/content-assets/ornament-small-rotate.png"
            alt="ornament"
          >
        </AnimateOnce>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="container mx-auto xs:px-4">
      <div class="max-w-[200px] h-[1px] bg-neutral-200 mx-auto mb-[80px] block" />
      <div id="testimoni">
        <AnimateOnce initial-class="opacity-0 translate-y-8" animated-class="opacity-100 translate-y-0" :duration="600">
          <p class="text-base text-center text-neutral-600 font-geist">
            Lihat kenapa mereka memilih dan tetap bersama
            <span class="font-semibold text-neutral-800">Rintisan</span>
          </p>
          <h2 class="mt-2 text-center font-medium text-[32px] text-neutral-800 font-geist">
            Kata Mereka
          </h2>
        </AnimateOnce>

        <!-- Stacked cards + side arrows -->
        <div class="max-w-3xl mx-auto">
          <div
            class="flex items-center gap-4 xs:gap-2 mt-10"
            @mouseenter="stopAutoplay"
            @mouseleave="startAutoplay"
          >
            <!-- Prev arrow -->
            <button
              type="button"
              class="shrink-0 bg-white border border-neutral-200 shadow-sm rounded-full flex justify-center items-center w-9 h-9 hover:bg-neutral-50 transition-colors"
              @click="prev(); stopAutoplay(); startAutoplay()"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18l-6-6 6-6" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>

            <!-- Stacked card -->
            <div class="relative flex-1">
              <div class="absolute inset-0 translate-x-4 translate-y-4 bg-neutral-100 border border-neutral-200 rounded-2xl" />
              <div class="absolute inset-0 translate-x-2 translate-y-2 bg-neutral-50 border border-neutral-200 rounded-2xl" />
              <div class="relative z-10 bg-white border border-neutral-200 rounded-2xl shadow-sm">
                <Transition name="fade" mode="out-in">
                  <div :key="currentIndex" class="px-10 py-6 xs:px-5 xs:py-6 min-h-[280px] xs:min-h-[240px] flex flex-col justify-between">
                    <p class="text-base leading-relaxed text-neutral-700 font-geist">
                      {{ currentTestimonial.content }}
                    </p>
                    <div class="mt-8">
                      <p class="font-bold text-neutral-800 font-geist">
                        {{ currentTestimonial.name }}
                      </p>
                      <p class="text-sm text-neutral-500 mt-1 font-geist">
                        {{ currentTestimonial.job }}
                      </p>
                    </div>
                  </div>
                </Transition>
              </div>
            </div>

            <!-- Next arrow -->
            <button
              type="button"
              class="shrink-0 bg-white border border-neutral-200 shadow-sm rounded-full flex justify-center items-center w-9 h-9 hover:bg-neutral-50 transition-colors"
              @click="next(); stopAutoplay(); startAutoplay()"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18l6-6-6-6" stroke="#292D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>

          <!-- Dots -->
          <div class="flex items-center justify-center gap-2 mt-6 pb-[80px]">
            <button
              v-for="(_, i) in testimonialList"
              :key="i"
              class="rounded-full transition-all duration-300"
              :class="currentIndex === i ? 'w-5 h-2 bgGradientV2' : 'w-2 h-2 bg-neutral-300'"
              @click="goTo(i); stopAutoplay(); startAutoplay()"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
